import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import PERMISSIONS from '@/constants/auth.constants';
import {
  ASSET_DATA_PROVIDER_DIALOG_LABELS,
  ASSET_DATA_PROVIDER_DIALOG_MACHINES,
  ASSET_DATA_PROVIDERS_OBJECT,
  EMPTY_ASSET_DATA_PROVIDER_DIALOG
} from '@/features/AdminAssetDataProviderIntegration/AssetDataProviderIntegration.constants';
import {
  assetIntegrationsDialogModeAtom,
  assetIntegrationsDialogOpenAtom,
  selectedAssetIntegrationAtom,
  useCreateAssetIntegration,
  useUpdateAssetIntegration
} from '@/features/AdminAssetDataProviderIntegration/AssetDataProviderIntegration.store';
import type { AssetIntegrationCreateDTO } from '@/features/AdminAssetDataProviderIntegration/AssetDataProviderIntegration.types';
import { ASSET_DATA_PROVIDERS } from '@/pages/Assets/Assets.constants';

import useStyles from './AssetDataProviderIntegration.styles';

const AssetDataProviderDialog = () => {
  const [assetIntegrationsDialogMode, setAssetIntegrationsDialogMode] = useAtom(
    assetIntegrationsDialogModeAtom
  );
  const [assetIntegrationsDialogOpen, setAssetIntegrationsDialogOpen] = useAtom(
    assetIntegrationsDialogOpenAtom
  );
  const [selectedAssetIntegration] = useAtom(selectedAssetIntegrationAtom);
  const { classes } = useStyles();

  const form = useForm({
    defaultValues: EMPTY_ASSET_DATA_PROVIDER_DIALOG
  });
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    reset,
    resetField,
    setValue
  } = form;
  const managed = useWatch({ control, name: 'managed' });
  const provider = useWatch({ control, name: 'provider' });

  const handleClose = () => {
    setAssetIntegrationsDialogOpen(false);
    setAssetIntegrationsDialogMode(null);
    reset();
  };
  const { createAssetIntegration, isLoadingCreateAssetIntegration } =
    useCreateAssetIntegration(handleClose);
  const { updateAssetIntegration, isLoadingUpdateAssetIntegration } =
    useUpdateAssetIntegration(handleClose);

  const DATA_PROVIDERS = ASSET_DATA_PROVIDERS_OBJECT.filter(
    (dataProvider) => dataProvider.value !== ASSET_DATA_PROVIDERS.MANUAL
  );

  // TODO: cleanup conditional form field logic (create config object)
  const MANAGED = managed;
  const IS_VMPE = provider === ASSET_DATA_PROVIDERS.VMPE;
  const IS_SECURITYSNARES = provider === ASSET_DATA_PROVIDERS.SECURITYSNARES;
  const MANAGED_AND_S1 =
    managed && provider === ASSET_DATA_PROVIDERS.SENTINELONE;
  const MANAGED_OR_VMPE_OR_DEFENDER =
    managed || IS_VMPE || provider === ASSET_DATA_PROVIDERS.DEFENDER;
  const NOT_MANAGED_AND_NOT_VMPE = !managed && !IS_VMPE && !IS_SECURITYSNARES;
  const NOT_MANAGED_OR_NOT_S1 =
    !managed || provider !== ASSET_DATA_PROVIDERS.SENTINELONE;
  const NOT_MANAGED_OR_VMPE = !managed || IS_VMPE || IS_SECURITYSNARES;
  //-----------------------------------------------------------------
  const dialogLabels = useMemo(() => {
    return (
      ASSET_DATA_PROVIDER_DIALOG_LABELS[
        assetIntegrationsDialogMode === 'create'
          ? (provider as keyof typeof ASSET_DATA_PROVIDER_DIALOG_LABELS)
          : // biome-ignore lint/correctness/noUnsafeOptionalChaining: an undefined index yields undefined and falls through to the default labels via `||`
            (selectedAssetIntegration?.provider as keyof typeof ASSET_DATA_PROVIDER_DIALOG_LABELS)
      ] || {
        tenant_id: 'Tenant ID',
        base_url: 'Base URL',
        api_token: 'API Token'
      }
    );
  }, [assetIntegrationsDialogMode, provider]);

  const dialogMachines = useMemo(() => {
    return provider in ASSET_DATA_PROVIDER_DIALOG_MACHINES
      ? ASSET_DATA_PROVIDER_DIALOG_MACHINES[
          provider as keyof typeof ASSET_DATA_PROVIDER_DIALOG_MACHINES
        ]
      : [];
  }, [provider]);

  const dialogTitle = useMemo(() => {
    return assetIntegrationsDialogMode === 'create'
      ? `Add Asset Integration`
      : `Edit Asset Integration (#${selectedAssetIntegration?.id})`;
  }, [assetIntegrationsDialogMode]);

  useEffect(() => {
    // reset api_token and base_url if managed
    if (MANAGED) {
      resetField('api_token');
      resetField('base_url');
    }

    // reset machine_name value if field no longer displayed
    if (NOT_MANAGED_OR_NOT_S1) {
      resetField('machine_name');
    }
  }, [managed, provider]);

  useEffect(() => {
    // prepopulate field when editing
    if (assetIntegrationsDialogMode === 'edit') {
      reset({
        ...EMPTY_ASSET_DATA_PROVIDER_DIALOG,
        api_token: selectedAssetIntegration?.api_token ?? '',
        disabled: selectedAssetIntegration?.disabled ?? false,
        managed: selectedAssetIntegration?.managed ?? false
      });
    }

    // clear form when mode changes to 'create'
    if (assetIntegrationsDialogMode === 'create') {
      reset(EMPTY_ASSET_DATA_PROVIDER_DIALOG);
    }
  }, [assetIntegrationsDialogOpen, selectedAssetIntegration, reset, setValue]);

  const onSubmit = handleSubmit((data: AssetIntegrationCreateDTO) => {
    if (assetIntegrationsDialogMode === 'edit') {
      const changedFields = {} as any;

      Object.keys(dirtyFields).forEach((key) => {
        if (dirtyFields[key as keyof typeof dirtyFields]) {
          changedFields[key] = data[key as keyof AssetIntegrationCreateDTO];
        }
      });
      return updateAssetIntegration(changedFields);
    }

    return createAssetIntegration(data);
  });

  return (
    <Dialog
      open={assetIntegrationsDialogOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <FormProvider {...form}>
          <form onSubmit={onSubmit}>
            <Box sx={{ my: 1 }}>
              <Grid container spacing={2}>
                {assetIntegrationsDialogMode === 'create' && (
                  <>
                    <Grid
                      container
                      size={{
                        xs: 12,
                        md: MANAGED_AND_S1 ? 6 : 12
                      }}
                    >
                      <div className={classes.assetDataProviderFormElement}>
                        <InputLabel htmlFor="provider-select" required>
                          Provider
                        </InputLabel>
                        <FormControl id="provider-select" fullWidth>
                          <Controller
                            control={control}
                            defaultValue=""
                            name="provider"
                            rules={{
                              required: true
                            }}
                            render={({ field }) => (
                              <Select
                                id="provider-select"
                                error={!!errors?.provider}
                                {...field}
                              >
                                {DATA_PROVIDERS.map((assetDataProvider) => (
                                  <MenuItem
                                    key={assetDataProvider.id}
                                    value={assetDataProvider.value}
                                  >
                                    {assetDataProvider.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </FormControl>
                      </div>
                      <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
                        {assetIntegrationsDialogMode === 'create' &&
                          !IS_VMPE &&
                          !IS_SECURITYSNARES &&
                          provider && (
                            <Controller
                              name="managed"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <FormControlLabel
                                  control={
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={value || false}
                                          onChange={onChange}
                                          sx={{ ml: 1 }}
                                        />
                                      }
                                      label="Data provider managed by Pondurance."
                                    />
                                  }
                                  label=""
                                />
                              )}
                            />
                          )}
                      </CheckPermissions>
                    </Grid>
                    {MANAGED_AND_S1 && (
                      <Grid container size={{ xs: 12, md: 6 }}>
                        <div className={classes.assetDataProviderFormElement}>
                          <InputLabel htmlFor="machine-name-select" required>
                            Machine
                          </InputLabel>
                          <FormControl fullWidth>
                            <Controller
                              control={control}
                              defaultValue=""
                              name="machine_name"
                              rules={{
                                required: true
                              }}
                              render={({ field }) => (
                                <Select
                                  id="machine-name-select"
                                  defaultValue={null}
                                  error={!!errors?.machine_name}
                                  {...field}
                                >
                                  {dialogMachines.map((machine) => (
                                    <MenuItem
                                      key={machine.id}
                                      value={machine.value}
                                    >
                                      {machine.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </FormControl>
                        </div>
                      </Grid>
                    )}
                    {!IS_SECURITYSNARES && (
                      <Grid container size={{ xs: 12 }}>
                        <div className={classes.assetDataProviderFormElement}>
                          <InputLabel
                            htmlFor="tenant-id-input"
                            required={MANAGED_OR_VMPE_OR_DEFENDER}
                          >
                            {dialogLabels.tenant_id}
                          </InputLabel>
                          <FormControl fullWidth>
                            <Controller
                              control={control}
                              name="tenant_id"
                              rules={{
                                required: MANAGED_OR_VMPE_OR_DEFENDER
                              }}
                              render={({ field }) => (
                                <TextField
                                  id="tenant-id-input"
                                  error={!!errors?.tenant_id}
                                  {...field}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                      </Grid>
                    )}
                  </>
                )}
                {NOT_MANAGED_AND_NOT_VMPE &&
                  assetIntegrationsDialogMode === 'create' && (
                    <Grid container size={{ xs: 12 }}>
                      <div className={classes.assetDataProviderFormElement}>
                        <InputLabel htmlFor="base-url-input" required>
                          {dialogLabels.base_url}
                        </InputLabel>
                        <FormControl fullWidth>
                          <Controller
                            control={control}
                            name="base_url"
                            rules={{
                              required: true
                            }}
                            render={({ field }) => (
                              <TextField
                                id="base-url-input"
                                error={!!errors?.base_url}
                                sx={{ width: '100%' }}
                                {...field}
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                    </Grid>
                  )}
                {NOT_MANAGED_OR_VMPE && (
                  <Grid container size={{ xs: 12 }}>
                    <div className={classes.assetDataProviderFormElement}>
                      <InputLabel htmlFor="api-token-input" required>
                        {dialogLabels.api_token}
                      </InputLabel>
                      <FormControl fullWidth>
                        <Controller
                          control={control}
                          name="api_token"
                          rules={{
                            required: true
                          }}
                          render={({ field }) => (
                            <TextField
                              id="api-token-input"
                              error={!!errors?.api_token}
                              sx={{ width: '100%' }}
                              {...field}
                            />
                          )}
                        />
                      </FormControl>
                    </div>
                  </Grid>
                )}
                <Grid container size={{ xs: 12 }}>
                  <div className={classes.assetDataProviderFormElement}>
                    <InputLabel htmlFor="disabled-input">Disabled</InputLabel>
                    <FormControl fullWidth>
                      <Controller
                        control={control}
                        name="disabled"
                        render={({ field }) => (
                          <FormControlLabel
                            slotProps={{
                              typography: {
                                sx: { fontWeight: 'bold', width: '8rem' }
                              }
                            }}
                            control={
                              <Switch
                                checked={field.value}
                                {...field}
                                onChange={() => field.onChange(!field.value)}
                              />
                            }
                            label="Integration Disabled"
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                </Grid>
              </Grid>
            </Box>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          type="submit"
          onClick={onSubmit}
          disabled={
            isLoadingCreateAssetIntegration || isLoadingUpdateAssetIntegration
          }
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetDataProviderDialog;

//-----------------------------------------------------------------
// Docs - as of 10/14/2025 - form field requirements
// CROWDSTRIKE - UNMANAGED
//  - Client ID
//  - Base URL [REQUIRED]
//  - Client Secret [REQUIRED]

// CROWDSTRIKE - MANAGED
//  - Client ID [REQUIRED]
// -------------------------------------------

// SENTINELONE - UNMANAGED
//  - Tenant ID
//  - Base URL [REQUIRED]
//  - API Token [REQUIRED]

// SENTINELONE - MANAGED
//  - Tenant ID [REQUIRED]
//  - Machine [REQUIRED]
// -------------------------------------------

// VMPE - UNMANAGED
//  - Tenant ID [REQUIRED]
//  - API Token [REQUIRED]

// VMPE - MANAGED
// * not applicable
